import React, { useState } from 'react';
import { CalendarDays, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AppointmentBooking = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !formData.name || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please select a date, time, and fill in your details",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const appointmentData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: selectedDate.toISOString(),
        time: selectedTime,
        notes: formData.notes
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/appointments`, appointmentData);
      toast({
        title: "Thank You for Booking! 🎉",
        description: `Your appointment for ${selectedDate.toLocaleDateString()} at ${selectedTime} is confirmed. Check your email for details.`,
        duration: 6000
      });
      setSelectedDate(null);
      setSelectedTime(null);
      setFormData({ name: '', email: '', phone: '', notes: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to book appointment. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '16px 20px',
    border: '1px solid var(--border-medium)',
    borderRadius: '0px',
    fontSize: '14px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    transition: 'border-color 0.2s ease'
  };

  const availableTimes = ['09:00 AM', '10:00 AM', '11:00 AM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'];

  return (
    <section id="appointments" className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
      <div className="luxury-container">
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div style={{ marginBottom: '16px' }}>
            <CalendarDays size={40} strokeWidth={1.5} color="var(--accent-gold)" style={{ margin: '0 auto' }} />
          </div>
          <h2 className="heading-1" style={{ marginBottom: '16px' }}>
            Book Your Consultation
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Schedule a personalized consultation to discuss your tax preparation needs
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid-two-column" style={{ marginBottom: '40px' }}>
            <div>
              <h3 className="heading-3" style={{ marginBottom: '24px' }}>
                Select Date & Time
              </h3>
              
              <div style={{ marginBottom: '32px' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '12px', color: 'var(--text-primary)' }}>
                  Choose a Date
                </label>
                <div style={{ 
                  border: '1px solid var(--border-light)', 
                  borderRadius: '0px',
                  padding: '20px',
                  background: 'var(--bg-primary)'
                }}>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    className="luxury-calendar"
                  />
                </div>
              </div>

              {selectedDate && (
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '12px', color: 'var(--text-primary)' }}>
                    Available Times
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                    {availableTimes.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        style={{
                          padding: '12px 16px',
                          border: `1px solid ${selectedTime === time ? 'var(--interactive-base)' : 'var(--border-medium)'}`,
                          borderRadius: '0px',
                          background: selectedTime === time ? 'var(--interactive-base)' : 'var(--bg-primary)',
                          color: selectedTime === time ? 'var(--bg-primary)' : 'var(--text-primary)',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <Clock size={14} />
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className="heading-3" style={{ marginBottom: '24px' }}>
                Your Information
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={isSubmitting}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={isSubmitting}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={isSubmitting}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                  />
                </div>
                
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    Additional Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={isSubmitting}
                    rows="4"
                    style={{...inputStyle, resize: 'vertical'}}
                    onFocus={(e) => e.target.style.borderColor = 'var(--interactive-base)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium)'}
                  />
                </div>
                
                <button
                  type="submit"
                  className="btn-primary gold-accent"
                  disabled={isSubmitting}
                  style={{ width: '100%' }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Booking...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Book Appointment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AppointmentBooking;